import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import { DemoMediaList } from '../dtos/demoMedia';
import { accessToken, getAuthDetails } from '../utils/helper';
import Cookies from 'universal-cookie';

const cookies = new Cookies(null, { path: '/' });

interface DemoMediaState {
  demoMediaList:
    | { data: DemoMediaList[]; total: number; totalPages: number }
    | { data: []; total: 0; totalPages: 0 };
  loading: boolean;
  error: string | null;
  addDemoMediaResponse: { message: string; success: boolean };
  addDemoMediaError: string | null;
  updateDemoMediaResponse: { message: string; success: boolean };
  updateDemoMediaError: string | null;
  deleteDemoMediaResponse: { message: string; success: boolean };
  deleteDemoMediaError: string | null;
}

const initialState: DemoMediaState = {
  demoMediaList: { data: [], total: 0, totalPages: 0 },
  addDemoMediaResponse: { message: '', success: false },
  updateDemoMediaResponse: { message: '', success: false },
  deleteDemoMediaResponse: { message: '', success: false },
  loading: false,
  error: null,
  addDemoMediaError: null,
  updateDemoMediaError: null,
  deleteDemoMediaError: null,
};

export const getAllDemoMediasTrigger = createAsyncThunk(
  'get/demo-media',
  async ({
    page,
    limit,
    search,
    clientId,
    sortOrder,
    sortField,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    clientId: number;
    sortOrder?: string;
    sortField?: string;
  }) => {
    try {
      const queryParams = new URLSearchParams();
      if (clientId) queryParams.append('clientId', clientId.toString());
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (sortField) queryParams.append('sortField', sortField);

      const response = await api.get(`/demo-media?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [], totalPages: 0 };
    }
  },
);

export const addDemoMediaTrigger = createAsyncThunk<
  { message: string; success: boolean },
  FormData
>('/demo-media/add', async (request: FormData) => {
  try {
    const response = await api.post<{ message: string, success: boolean }>(
      `/demo-media/add`,
      request,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${getAuthDetails().accessToken}`,
          'Content-Type': 'multipart/form-data',
          'x-csrf-token': cookies.get('XSRF-TOKEN'),
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return { message: 'Error adding user', success: false };
  }
});

export const updateDemoMediaTrigger = createAsyncThunk(
  '/demo-media/update/:id',
  async ({ id, request }: { id: number; request: FormData }) => {
    try {
      const response = await api.patch<{ message: string, success: boolean }>(
        `/demo-media/update/${id}`,
        request,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${getAuthDetails().accessToken}`,
            'Content-Type': 'multipart/form-data',
            'x-csrf-token': cookies.get('XSRF-TOKEN'),
          },
        },
      );
      return response.data;
    } catch (error) {
      console.log(error);
      return { message: '', success: false };
    }
  },
);

export const deleteDemoMediaTrigger = createAsyncThunk(
  '/demo-media/delete/:id',
  async (id?: number) => {
    try {
      const response = await api.delete<{ message: string, success: boolean }>(
        `/demo-media/delete/${id}`,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      console.log(error);
      return { message: '', success: false };
    }
  },
);

export const demoMediaBulkUploadTrigger = createAsyncThunk<
  { message: string },
  FormData
>('/demo-media/bulk-upload', async (request: FormData) => {
  try {
    const response = await api.post<{ message: string }>(
      `/demo-media/bulk-upload`,
      request,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${getAuthDetails().accessToken}`,
          'Content-Type': 'multipart/form-data',
          'x-csrf-token': cookies.get('XSRF-TOKEN'),
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return { message: 'Error adding user' };
  }
});
export const getCompletedDemoMediaFilesTrigger = createAsyncThunk(
  'demo-media/getCompletedFiles',
  async () => {
    try {
      const response = await api.get(`/demo-media/completed/files`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [] };
    }
  },
);

export const getPendingAndFailedDemoMediaFilesTrigger = createAsyncThunk(
  'demo-media/getPendingAndFailedFiles',
  async () => {
    try {
      const response = await api.get(`/demo-media/pending-failed/files`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [] };
    }
  },
);
// page, limit, search, clientId, sortOrder, sortField
export const exportDemoMediaToCsvTrigger = createAsyncThunk(
  'demo-media/export/csv',
  async (
    paginationDto: {
      page: number;
      limit: number;
      search: string;
      clientId: number;
      sortOrder: string;
      sortField: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get(`/demo-media/export/csv`, {
        params: paginationDto,
        responseType: 'blob', // Important for handling file downloads
        headers: {
          Authorization: `Bearer ${getAuthDetails().accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return rejectWithValue('Error exporting CSV');
    }
  },
);

// Create slice
const demoMediaSlice = createSlice({
  name: 'demoMedia',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllDemoMediasTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllDemoMediasTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.demoMediaList = action.payload;
      })
      .addCase(getAllDemoMediasTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load posts';
      })
      .addCase(addDemoMediaTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(addDemoMediaTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.addDemoMediaResponse = action.payload;
      })
      .addCase(addDemoMediaTrigger.rejected, (state, action) => {
        state.loading = false;
        state.addDemoMediaError = action.error.message || 'Failed to load posts';
      })
      .addCase(updateDemoMediaTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDemoMediaTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.updateDemoMediaResponse = action.payload;
      })
      .addCase(updateDemoMediaTrigger.rejected, (state, action) => {
        state.loading = false;
        state.updateDemoMediaError = action.error.message || 'Failed to load posts';
      })
      .addCase(deleteDemoMediaTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDemoMediaTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteDemoMediaResponse = action.payload;
      })
      .addCase(deleteDemoMediaTrigger.rejected, (state, action) => {
        state.loading = false;
        state.deleteDemoMediaError = action.error.message || 'Failed to load posts';
      })
      .addCase(exportDemoMediaToCsvTrigger.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportDemoMediaToCsvTrigger.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportDemoMediaToCsvTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export the reducer to be added to the store
export default demoMediaSlice.reducer;
